/** Normaliza un nombre para comparar: minúsculas, sin tildes, solo letras/dígitos y espacios simples. */
export function normalizeName(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

export interface NameParts {
  givenNames: string;
  paternalSurname?: string | null;
  maternalSurname?: string | null;
}

export function fullName(p: NameParts): string {
  return [p.givenNames, p.paternalSurname, p.maternalSurname]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

export function surnames(p: NameParts): string {
  return [p.paternalSurname, p.maternalSurname].map((s) => (s ?? '').trim()).filter(Boolean).join(' ');
}

/** Iniciales para avatares ("JF"). */
export function initials(p: NameParts): string {
  const first = (p.givenNames ?? '').trim()[0] ?? '';
  const last = (p.paternalSurname ?? '').trim()[0] ?? '';
  return (first + last).toUpperCase() || '?';
}

function trigrams(s: string): Set<string> {
  const set = new Set<string>();
  for (const word of normalizeName(s).split(' ')) {
    if (!word) continue;
    const padded = `  ${word} `;
    for (let i = 0; i + 3 <= padded.length; i++) set.add(padded.slice(i, i + 3));
  }
  return set;
}

/** Similitud por trigramas, equivalente a `similarity()` de pg_trgm (0..1). */
export function nameSimilarity(a: string, b: string): number {
  const ta = trigrams(a);
  const tb = trigrams(b);
  if (ta.size === 0 && tb.size === 0) return 0;
  let shared = 0;
  for (const t of ta) if (tb.has(t)) shared++;
  return shared / (ta.size + tb.size - shared);
}

const PARTICLES = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'san', 'santa', 'da', 'do', 'dos', 'das', 'van', 'von', 'di', 'mc', 'mac']);

/**
 * Divide "Apellido1 Apellido2" en paterno y materno respetando partículas
 * ("de la Cruz Vega" → ["de la Cruz", "Vega"]).
 */
export function splitSurnames(s: string | null | undefined): [string | null, string | null] {
  const tokens = (s ?? '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [null, null];
  const groups: string[][] = [];
  let current: string[] = [];
  for (const t of tokens) {
    current.push(t);
    if (!PARTICLES.has(t.toLowerCase())) {
      groups.push(current);
      current = [];
    }
  }
  if (current.length) {
    if (groups.length) groups[groups.length - 1]!.push(...current);
    else groups.push(current);
  }
  const paternal = groups[0]!.join(' ');
  const maternal = groups.slice(1).map((g) => g.join(' ')).join(' ');
  return [paternal, maternal || null];
}
